// SensitivityHeatmap.js — 2D grid: forward pricing (rows) x market share (columns).
// Color-coded cells (green=profit, red=loss).

import React from 'react';

function getColor(value) {
  if (value > 0) {
    const intensity = Math.min(value / 500000, 1);
    const g = Math.round(130 + intensity * 80);
    return `rgb(${220 - Math.round(intensity * 100)}, ${g}, ${220 - Math.round(intensity * 100)})`;
  }
  if (value < 0) {
    const intensity = Math.min(Math.abs(value) / 500000, 1);
    const r = Math.round(200 + intensity * 55);
    return `rgb(${r}, ${200 - Math.round(intensity * 80)}, ${200 - Math.round(intensity * 80)})`;
  }
  return '#f9fafb';
}

function formatCompact(value) {
  if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return value.toFixed(0);
}

export default function SensitivityHeatmap({
  grid = [],
  currentForwardBps,
  targetForwardBps,
  currentSharePct,
}) {
  if (!grid.length || !grid[0]?.cells?.length) {
    return (
      <div className="text-xs text-gray-400 text-center py-8">
        No sensitivity data available
      </div>
    );
  }

  const shareValues = grid[0].cells.map(c => c.market_share_pct);

  return (
    <div className="overflow-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr>
            <th className="text-left py-2 px-2 text-gray-500 font-medium border-b border-gray-100">
              Fwd (bps) \ Share (%)
            </th>
            {shareValues.map(share => (
              <th
                key={share}
                className={`text-center py-2 px-2 font-medium border-b border-gray-100 ${
                  share === currentSharePct ? 'text-blue-700 bg-blue-50' : 'text-gray-500'
                }`}
              >
                {share}%
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.map((row, rowIdx) => {
            const isCurrentFwd = row.forward_pricing_bps === currentForwardBps;
            const isTargetFwd = row.forward_pricing_bps === targetForwardBps;

            return (
              <tr key={rowIdx}>
                <td className={`py-1.5 px-2 font-mono border-b border-gray-50 ${
                  isCurrentFwd ? 'font-bold text-blue-700 bg-blue-50' :
                  isTargetFwd ? 'font-bold text-amber-700 bg-amber-50' :
                  'text-gray-600'
                }`}>
                  {row.forward_pricing_bps}
                  {isCurrentFwd && <span className="ml-1 text-[9px] text-blue-500">You</span>}
                  {isTargetFwd && <span className="ml-1 text-[9px] text-amber-500">FF</span>}
                </td>
                {row.cells.map((cell, cellIdx) => (
                  <td
                    key={cellIdx}
                    className="text-center py-1.5 px-2 font-mono border-b border-gray-50"
                    style={{ backgroundColor: getColor(cell.net_revenue) }}
                  >
                    <span className={cell.profitable ? 'text-emerald-900' : 'text-red-900'}>
                      {formatCompact(cell.net_revenue)}
                    </span>
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-3 text-[10px] text-gray-500">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgb(120, 210, 120)' }} />
          Profitable
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgb(255, 120, 120)' }} />
          Loss
        </div>
        {currentForwardBps != null && (
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-blue-100 border border-blue-300" />
            Your Price
          </div>
        )}
        {targetForwardBps != null && (
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-amber-100 border border-amber-300" />
            FF Target
          </div>
        )}
      </div>
    </div>
  );
}
